import { RefObject, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ScrollPill } from '@/components/atoms/ScrollPill/ScrollPill';

type Props = {
  containerRef: RefObject<HTMLDivElement | null>;
  selectedTurnIndex?: number;
  turnCount: number;
  isHumanAgent?: boolean;
};

const rowFor = (container: HTMLDivElement, index: number) =>
  container.querySelector<HTMLElement>(`[data-turn-row="${index}"]`);

/** Pill that appears when the selected turn has scrolled out of the timeline. */
export const ScrollToSelectedTurn = ({
  containerRef,
  selectedTurnIndex,
  turnCount,
  isHumanAgent,
}: Props) => {
  const { t } = useTranslation();
  const [direction, setDirection] = useState<'up' | 'down' | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || selectedTurnIndex == null) {
      setDirection(null);
      return undefined;
    }

    const check = () => {
      const row = rowFor(container, selectedTurnIndex);
      if (!row) {
        setDirection(null);
        return;
      }
      const view = container.getBoundingClientRect();
      const rect = row.getBoundingClientRect();
      if (rect.bottom > view.top && rect.top < view.bottom) {
        setDirection(null);
        return;
      }
      setDirection(rect.bottom <= view.top ? 'up' : 'down');
    };

    check();
    container.addEventListener('scroll', check, { passive: true });
    // Images loading and turns arriving move the row with no scroll to react to.
    const resize = new ResizeObserver(check);
    resize.observe(container);
    if (container.firstElementChild) {
      resize.observe(container.firstElementChild);
    }
    return () => {
      container.removeEventListener('scroll', check);
      resize.disconnect();
    };
  }, [containerRef, selectedTurnIndex, turnCount]);

  if (!direction || selectedTurnIndex == null) {
    return null;
  }

  return (
    <ScrollPill
      direction={direction}
      onClick={() => {
        const container = containerRef.current;
        const row = container && rowFor(container, selectedTurnIndex);
        row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }}
    >
      {isHumanAgent
        ? t('try_it.human_agent')
        : t('try_it.turn_number', { 0: selectedTurnIndex + 1 })}
    </ScrollPill>
  );
};
